/*
 * Mutations for the [board store]{link module:store/board}
 *
 * @module store/board/mutations
 */

import {
  compFs,
  CompRule,
  EvalTree,
  Operation,
  Group,
  findGroupInConfig,
  findEvalTreeNodeInConfig,
  Name,
  TYPE_TO_UNIT,
  findCompRuleInConfig
} from '@/store/modules/board'
import { v1 as uuidv1 } from 'uuid'

/**
 * Mutation to set the current Config
 *
 * @function
 *
 * @param {object} c has to be a [Group]
 *
 */
export function SET_CONFIG(state, c) {
  state.config = c
}

/**
 * Mutation to set the current Config with the matching measurements
 *
 * @function
 *
 * @param {object} c has to be a [Group]
 *
 */
export function SET_CONFIG_WITH_MEASUREMENTS(state, c) {
  state.config = c

  setMeasurements(state)
}

/**
 * Mutation to toggle the showChildren boolean of a given group
 *
 * finds the Group in the config and toggles the showChildren param
 *
 * @function
 *
 * @param {integer} gId the groupId of a Group in the config
 *
 */
export function TOGGLE_SHOW_CHILDREN(state, gId) {
  const g = findGroupInConfig(gId, state.config)
  if (g) {
    g.showChildren = !g.showChildren
  }
  setMeasurements(state)
}

/**
 * Mutation to set all the showChildren params in the config
 *
 * changes the showChildren params and adjusts the measurements
 *
 * @function
 *
 * @param {Boolean} showChildren
 *
 */
export function SET_ALL_SHOW_CHILDREN(state, showChildren) {
  const setChildrenShowChildren = (g, showChildren) => {
    g.showChildren = showChildren
    for (const child of g.children) {
      setChildrenShowChildren(child, showChildren)
    }
  }

  for (const subtree of state.config) {
    setChildrenShowChildren(subtree, showChildren)
  }
  setMeasurements(state)
}

/**
 * Mutation to create a new root node into the config
 *
 * Creates a new Default Group
 *
 * @function
 *
 */
export function CREATE_ROOT(state) {
  state.config.push(
    new Group({
      id: uuidv1(),
      name: new Name({ en: 'NEW ROOT', de: 'NEUE WURZEL' }),
      compRules: [],
      showChildren: true,
      icon: 'generic',
      children: []
    })
  )
}

/**
 * Mutation to create a new Group as childGroup
 *
 * finds a Group by Id, creates a new Default Group
 * adds the new Group as child
 *
 * @function
 *
 * @param {integer} gId
 *
 */
export function CREATE_CHILD_GROUP(state, gId) {
  const g = findGroupInConfig(gId, state.config)
  g.children.push(
    new Group({
      id: uuidv1(),
      name: new Name({ en: 'NEW NODE', de: 'NEUER KNOTEN' }),
      compRules: [],
      showChildren: true,
      icon: 'generic',
      children: []
    })
  )
}

/**
 * Mutation to set an existing group as childgroup
 *
 * finds a Group and a childGroup by Id
 * pushes the childgroup to the children of the group
 * creates a new root, in case the the childgroup was a predecessor and root
 *
 * @function
 *
 * @param {integer} gId
 * @param {integer} childId
 *
 */
export function MOVE_GROUP_AS_CHILD_GROUP(state, { gId, childId }) {
  const g = findGroupInConfig(gId, state.config)
  const c = findGroupInConfig(childId, state.config)

  const path = getPathTo(gId, state.config)
  const cIndex = path.indexOf(c)

  if (cIndex !== -1) {
    // c is a predecessor of g
    const postC = path[cIndex + 1]
    deleteGroupFromArray(postC.id, state.config)

    if (cIndex === 0) {
      // c was root => postC new root
      state.config.push(postC)
    } else {
      const preC = path[cIndex - 1]
      preC.children.push(postC)
    }
  }

  deleteGroupFromArray(childId, state.config)
  g.children.push(c)
}

/**
 * Mutation to move a Group to root
 *
 * finds a Group by Id and pushes it as root
 *
 * @function
 *
 * @param {integer} gId
 *
 */
export function MOVE_GROUP_TO_ROOT(state, { gId }) {
  const g = findGroupInConfig(gId, state.config)

  deleteGroupFromArray(gId, state.config)
  state.config.push(g)
}

/**
 * Mutation to update a Group
 *
 * finds a Group by Id and changes icon/name/showChildren
 *
 * @function
 *
 * @param {integer} gId
 * @param {String} icon
 * @param {String} name
 * @param {Boolean} showChildren
 *
 */
export function UPDATE_GROUP(state, { gId, icon = null, name = null, showChildren = null }) {
  const g = findGroupInConfig(gId, state.config)
  if (name) {
    g.name = new Name({ en: name, de: name })
  }

  g.icon = icon ?? g.icon
  g.showChildren = showChildren ?? g.showChildren
}

/**
 * Mutation to update the Position of a Group in its parents childrenarray
 *
 * finds a Group by Id and shifts its position one spot in a direction
 *
 * @function
 *
 * @param {integer} gId
 * @param {String} direction {'left' | 'right'}
 *
 */
export function UPDATE_GROUP_POSITION(state, { gId, direction }) {
  let shift = null
  if (direction === 'left') {
    shift = -1
  }
  if (direction === 'right') {
    shift = 1
  }
  if (shift) {
    moveGroupInArray(gId, state.config, shift)
  }
}

/**
 * Mutation to delete a Group
 *
 * finds a Group by Id and deletes it. including all children
 *
 * @function
 *
 * @param {integer} gId
 *
 */
export function DELETE_GROUP(state, gId) {
  deleteGroupFromArray(gId, state.config)
}

/**
 * Mutation to create a CompRule
 *
 * finds a Group by Id and adds a new Eval-CompRule
 *
 * @function
 *
 * @param {integer} gId
 * @param {String} name
 * @param {String} type, {'power' | 'soc' | 'temperature'}
 *
 */
export function CREATE_COMP_RULE(state, { gId, name, type }) {
  const g = findGroupInConfig(gId, state.config)

  let typeExists = false
  for (const c of g.compRules) {
    if (c.type === type) {
      typeExists = true
    }
  }
  if (!typeExists) {
    const c = new CompRule({
      name: new Name({ en: name, de: name }),
      operation: new Operation({
        operator: 'eval',
        references: null,
        tree: new EvalTree({})
      }),
      type,
      unit: TYPE_TO_UNIT[type]
    })
    g.compRules.push(c)
  }
}

/**
 * Mutation to change the name of a CompRule
 *
 * @function
 *
 * @param {integer} gId
 * @param {integer} cId
 * @param {String} name
 *
 */
export function CHANGE_COMP_RULE_NAME(state, { gId, cId, name }) {
  const c = findCompRuleInConfig(gId, cId, state.config)

  if (c) {
    c.name = new Name({ en: name, de: name })
  }
}

/**
 * Mutation to delete a CompRule
 *
 * @function
 *
 * @param {integer} gId
 * @param {integer} cId
 *
 */
export function DELETE_COMP_RULE(state, { gId, cId }) {
  const g = findGroupInConfig(gId, state.config)

  for (const c of g.compRules) {
    if (c.id === cId) {
      g.compRules.splice(g.compRules.indexOf(c), 1)
    }
  }
}

/**
 * Mutation to update a EvalTreeNode in a CompRule
 *
 * Finds a EvalTreeNode through Group => CompRule => EvalTreeNode
 * changes the node based on the given parameter
 * operator => Change the operator, set reference and value to null
 *          => if left, right not existing, creates Default Nodes
 * reference => Change the reference, set operator and value to null
 * value => change the value, set operator and reference to null
 *
 * @function
 *
 * @param {integer} gId
 * @param {integer} cId
 * @param {integer} eId
 * @param {String} operator
 * @param {String} reference
 * @param {Number} value
 *
 */
export function UPDATE_EVAL_TREE_NODE(state, { gId, cId, eId, operator = null, reference = null, value = null }) {
  const t = findEvalTreeNodeInConfig(gId, cId, eId, state.config)

  if (t) {
    if (operator) {
      t.operator = operator
      t.reference = null
      t.value = null
      if (!t.left) {
        t.left = new EvalTree({})
      }
      if (!t.right) {
        t.right = new EvalTree({})
      }
    } else if (reference) {
      t.operator = null
      t.reference = reference
      t.value = null
      t.left = null
      t.right = null
    } else if (value) {
      t.operator = null
      t.reference = null
      t.value = value
      t.left = null
      t.right = null
    }
  }
}

/**
 * Mutation to delete a EvalTreeNode in a CompRule
 *
 * Finds a EvalTreeNode through Group => CompRule => EvalTreeNode
 * sets all parameters of the Node to null
 *
 * @function
 *
 * @param {integer} gId
 * @param {integer} cId
 * @param {integer} eId
 *
 */
export function DELETE_EVAL_TREE_NODE(state, { gId, cId, eId }) {
  const t = findEvalTreeNodeInConfig(gId, cId, eId, state.config)

  if (t) {
    t.operator = null
    t.reference = null
    t.value = null
    t.left = null
    t.right = null
  }
}

/**
 *
 * Helper Function to create the measurements function for the config
 *
 */
function setMeasurements(state) {
  const getOperations = (g) => {
    const operations = {
      [g.id]: {}
    }
    for (const compRule of g.compRules) {
      operations[g.id][compRule.id] = compRule.operation
    }

    if (!g.showChildren) {
      return operations
    }

    for (const child of g.children) {
      Object.assign(operations, getOperations(child))
    }
    return operations
  }

  const prep = {}
  for (const group of state.config) {
    Object.assign(prep, getOperations(group))
  }

  state.measurements = (vals) => {
    const res = {}
    for (const gId in prep) {
      res[gId] = {}
      for (const cId in prep[gId]) {
        res[gId][cId] = compFs[prep[gId][cId].operator](vals, prep[gId][cId])
      }
    }
    return res
  }
}

/**
 *
 * Helper Function to return the path to a given Group.
 *
 * @return [Group]Array [RootGroup, ..., Group(gId)]
 *
 */
const getPathTo = (gId, gs) => {
  for (const g of gs) {
    if (gId === g.id) {
      return [g]
    } else {
      const path = getPathTo(gId, g.children)
      if (path.length) {
        path.unshift(g)
        return path
      }
    }
  }
  return []
}

/**
 *
 * Helper Function to remove a Group from its parents children array.
 *
 */
const deleteGroupFromArray = (gId, gs) => {
  for (const g of gs) {
    if (g.id === gId) {
      gs.splice(gs.indexOf(g), 1)
      return
    } else {
      deleteGroupFromArray(gId, g.children)
    }
  }
}

/**
 *
 * Helper Function to shift the index of a group in the parents childarray
 *
 */
const moveGroupInArray = (gId, gs, shift) => {
  for (const g of gs) {
    if (g.id === gId) {
      const index = gs.indexOf(g)
      const shiftIndex = index + shift

      if (gs[shiftIndex]) {
        const tmp = g
        gs[index] = gs[shiftIndex]
        gs[shiftIndex] = tmp
      }

      return
    } else {
      moveGroupInArray(gId, g.children, shift)
    }
  }
}
